import {URLParameters} from '@gongxigames/framework/core/fragments/URLParameters';
import {dataUrlToBlob} from '@gongxigames/framework/core/utils/binaryData';
import {SplashScreenConfig} from '@gongxigames/framework/slot/types/EnvironmentProperty';

const SPLASH_SCREENS = ['none', 'pop', 'lottostar_prive', 'default_koala', 'default_ruby'] as const;

type Splash = (typeof SPLASH_SCREENS)[number];

const SCREENS_MAP: Record<Splash, SplashScreenConfig> = {
    lottostar_prive: {
        logo: {
            src: 'lottostar_prive.gif',
            duration: 2000,
            width: 750
        }
    },

    default_ruby: {
        logo: {
            src: 'default.gif',
            duration: 2000,
            width: 750
        }
    },
    default_koala: {
        logo: {
            src: 'koala_games_logo_small.gif',
            duration: 2000,
            width: 750
        }
    },
    pop: {
        logo: {
            src: 'Playtech_logo.png',
            duration: 2000,
            width: 744
        },
        watermark: {
            src: 'Powered_by_Playtech.png',
            duration: 2500,
            width: 744
        }
    },
    none: {
        logo: {
            src: '',
            duration: 0,
            width: 0
        }
    }
};

export class SplashScreen {
    private readonly splashScreenId = 'splash_screen';
    private readonly urlParams = new URLParameters(location.search);
    private readonly splashType = this.getSplashType();
    private readonly browser = window;
    private element?: HTMLDivElement;
    private elementSplashImg?: HTMLImageElement;
    private awaitHide?: Promise<void>;

    constructor() {
        if (this.splashType === 'none') {
            return;
        }

        this.createSplashScreenContainer();
        this.awaitHide = this.getHideTimeOutPromise();
    }

    public restore(): void {
        if (!this.element) {
            return;
        }

        document.body.appendChild(this.element);
    }

    public removeFromDOM(): void {
        this.element?.parentElement?.removeChild(this.element);
    }

    public awaitSplashHide(): Promise<void> | undefined {
        return this.awaitHide;
    }

    private createSplashScreenContainer() {
        const source = this.getSplashSource();
        let container = document.getElementById(this.splashScreenId) as HTMLDivElement;

        if (container) {
            return;
        }

        container = document.createElement('div');
        container.id = this.splashScreenId;
        container.style.display = 'block';
        container.style.position = 'fixed';
        container.style.top = '0';
        container.style.left = '0';
        container.style.width = '100%';
        container.style.height = '100%';
        container.style.zIndex = '100000';
        container.style.overflow = 'hidden';
        container.style.backgroundRepeat = 'no-repeat';
        container.style.backgroundPosition = 'center';
        container.style.backgroundColor = 'black';

        const splash = document.createElement('img');

        this.elementSplashImg = splash;
        splash.src = this.dataToSrc(source.logo.src);
        splash.style.position = 'absolute';
        splash.style.top = '50%';
        splash.style.left = '50%';
        splash.style.maxWidth = '100%';
        splash.style.width = `${source.logo.width}px`;
        splash.style.height = 'auto';
        splash.style.transform = 'translate(-50%, -50%)';

        if (source.watermark) {
            const watermark = document.createElement('img');

            watermark.src = this.dataToSrc(source.watermark.src);
            watermark.style.position = 'absolute';
            watermark.style.bottom = '20px';
            watermark.style.right = '20px';
            watermark.style.maxWidth = '40%';
            watermark.style.width = `${source.watermark.width}px`;
            watermark.style.height = 'auto';

            container.appendChild(watermark);
        }

        container.appendChild(splash);
        document.body.appendChild(container);

        this.element = container;
    }

    private async getHideTimeOutPromise(): Promise<void> {
        await new Promise(res => {
            if (!this.elementSplashImg || this.elementSplashImg?.complete) {
                return res(null);
            }

            this.elementSplashImg.addEventListener('load', () => res(null));
            this.elementSplashImg.addEventListener('error', () => res(null));
        });

        return new Promise(res => {
            this.browser.setTimeout(res, this.getSplashSource().logo.duration);
        });
    }

    private getSplashType(): Splash {
        const splashType = this.urlParams.getParameter('splashScreen');

        if (SPLASH_SCREENS.includes(splashType)) {
            return splashType;
        }

        if (window.RP_CONFIG.buildProperty.provider === 'koala') {
            return 'default_koala';
        }

        return 'default_ruby';
    }

    private getSplashSource(): SplashScreenConfig {
        return window.RP_CONFIG.splashScreenConfig || SCREENS_MAP[this.splashType];
    }

    private dataToSrc(data: string): string {
        const isBase64 = data.slice(0, 5) === 'data:';

        if (!isBase64) {
            return this.getFullUrl(data);
        }

        return dataUrlToBlob(data);
    }

    private getFullUrl(file: string): string {
        return `${window.RP_CONFIG.root}/static/splash_screen/${file}`;
    }
}
